/**
 * NotificationAdminPage — `/admin/notifications`.
 *
 * Two tabs:
 *   - Queue: live DLQ entries from `GET /v1/notifications/dlq` on
 *     service-notification, with a per-row Replay action wired to
 *     `POST /v1/notifications/dlq/:id/replay`.
 *   - Templates: notification template catalogue from
 *     `GET /v1/notifications/templates`.
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  listNotificationDlq,
  listNotificationTemplates,
  replayNotificationDlqEntry,
  type NotificationDlqEntry,
  type NotificationDlqFailureKind,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

const FAILURE_KIND_PILL: Record<NotificationDlqFailureKind, string> = {
  VALIDATION_FAIL: 'bg-warning-light text-warning',
  DESERIALIZE_FAIL: 'bg-danger-light text-danger',
  HANDLER_ERROR: 'bg-danger-light text-danger',
};

const REASON_TRUNCATE = 80;

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

interface ErrorBannerInfo {
  readonly kind: 'auth' | 'unreachable';
  readonly title: string;
  readonly detail: string;
}

function classifyError(err: unknown): ErrorBannerInfo {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      return {
        kind: 'auth',
        title: 'Permission denied',
        detail:
          err.status === 401
            ? 'Your session is no longer authenticated. Sign in again to view the DLQ.'
            : 'Your account is missing the Notifications/readDlq permission.',
      };
    }
  }
  return {
    kind: 'unreachable',
    title: 'Notification service unreachable',
    detail: err instanceof Error ? err.message : 'Unknown error',
  };
}

export function NotificationAdminPage(): JSX.Element {
  const [tab, setTab] = useState<'queue' | 'templates'>('queue');
  const [topicFilter, setTopicFilter] = useState<string>('');
  const [kindFilter, setKindFilter] = useState<'' | NotificationDlqFailureKind>('');

  const queryClient = useQueryClient();

  const dlq = useQuery({
    queryKey: ['notifications', 'dlq', { topic: topicFilter || undefined }],
    queryFn: () =>
      listNotificationDlq({
        topic: topicFilter || undefined,
        page: 1,
      }),
    retry: false,
    staleTime: 15_000,
    enabled: tab === 'queue',
  });

  const templates = useQuery({
    queryKey: ['notifications', 'templates', 'notification-admin'],
    queryFn: () => listNotificationTemplates(),
    retry: false,
    staleTime: 60_000,
    enabled: tab === 'templates',
  });

  const replay = useMutation({
    mutationFn: (id: string) => replayNotificationDlqEntry(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'dlq'] });
    },
  });

  // Unique topics for the dropdown — derived from the loaded page so operators
  // can drill down without typing.
  const topicOptions = useMemo<readonly string[]>(() => {
    const items = dlq.data?.items ?? [];
    const set = new Set<string>();
    for (const row of items) set.add(row.sourceTopic);
    return Array.from(set).sort();
  }, [dlq.data?.items]);

  const filteredRows = useMemo<readonly NotificationDlqEntry[]>(() => {
    const rows = dlq.data?.items ?? [];
    if (!kindFilter) return rows;
    return rows.filter((r) => r.failureKind === kindFilter);
  }, [dlq.data?.items, kindFilter]);

  function onReplay(row: NotificationDlqEntry): void {
    const ok = window.confirm(
      `Replay this DLQ entry? Original payload will be re-published to ${row.sourceTopic}.`,
    );
    if (!ok) return;
    replay.mutate(row.id);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notification admin"
        description="Dead-letter queue + notification template catalogue."
      />

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          type="button"
          onClick={() => setTab('queue')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${tab === 'queue' ? 'bg-white text-nipost-green shadow-sm' : 'text-muted'}`}
        >
          Queue
        </button>
        <button
          type="button"
          onClick={() => setTab('templates')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${tab === 'templates' ? 'bg-white text-nipost-green shadow-sm' : 'text-muted'}`}
        >
          Templates
        </button>
      </div>

      {tab === 'queue' ? (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={topicFilter}
              onChange={(e) => setTopicFilter(e.target.value)}
              aria-label="Topic filter"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            >
              <option value="">All topics</option>
              {topicOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as typeof kindFilter)}
              aria-label="Failure kind filter"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            >
              <option value="">All failure kinds</option>
              <option value="VALIDATION_FAIL">Validation fail</option>
              <option value="DESERIALIZE_FAIL">Deserialize fail</option>
              <option value="HANDLER_ERROR">Handler error</option>
            </select>
            {dlq.isFetching ? (
              <span className="text-xs text-muted">Refreshing…</span>
            ) : null}
          </div>

          {dlq.isError ? (
            (() => {
              const info = classifyError(dlq.error);
              return (
                <div
                  className={`rounded-lg border p-3 text-xs ${
                    info.kind === 'auth'
                      ? 'bg-yellow-50 border-yellow-200 text-yellow-900'
                      : 'bg-red-50 border-red-200 text-red-900'
                  }`}
                >
                  <p className="font-semibold">{info.title}</p>
                  <p>{info.detail}</p>
                </div>
              );
            })()
          ) : null}

          {replay.isError ? (
            (() => {
              const info = classifyError(replay.error);
              return (
                <div
                  className={`rounded-lg border p-3 text-xs ${
                    info.kind === 'auth'
                      ? 'bg-yellow-50 border-yellow-200 text-yellow-900'
                      : 'bg-red-50 border-red-200 text-red-900'
                  }`}
                >
                  <p className="font-semibold">
                    {info.kind === 'auth' ? 'Replay denied' : 'Replay failed'}
                  </p>
                  <p>{info.detail}</p>
                </div>
              );
            })()
          ) : null}

          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
                <tr>
                  <th className="p-3 text-left">Failure time</th>
                  <th className="p-3 text-left">Source topic</th>
                  <th className="p-3 text-right">Partition</th>
                  <th className="p-3 text-left">Failure kind</th>
                  <th className="p-3 text-left">Reason</th>
                  <th className="p-3 text-right">Replays</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {dlq.isPending && tab === 'queue' ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-muted text-xs">
                      Loading DLQ…
                    </td>
                  </tr>
                ) : !dlq.isError && filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-muted text-xs">
                      No DLQ entries.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const truncated = truncate(row.failureReason, REASON_TRUNCATE);
                    return (
                      <tr key={row.id} className="border-t border-border hover:bg-gray-50">
                        <td className="p-3 text-xs">{formatTimestamp(row.dlqAt)}</td>
                        <td className="p-3 font-mono text-xs">{row.sourceTopic}</td>
                        <td className="p-3 text-right text-xs font-mono">{row.partition}</td>
                        <td className="p-3 text-xs">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${FAILURE_KIND_PILL[row.failureKind]}`}
                          >
                            {row.failureKind}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-muted" title={row.failureReason}>
                          {truncated}
                        </td>
                        <td className="p-3 text-right text-xs font-mono">{row.replayCount}</td>
                        <td className="p-3 text-right">
                          <button
                            type="button"
                            onClick={() => onReplay(row)}
                            disabled={replay.isPending && replay.variables === row.id}
                            className="px-3 py-1 text-xs font-medium rounded-md bg-nipost-green text-white hover:opacity-90 disabled:opacity-50"
                          >
                            {replay.isPending && replay.variables === row.id
                              ? 'Replaying…'
                              : 'Replay'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {templates.isPending ? (
            <p className="text-sm text-muted">Loading templates…</p>
          ) : templates.isError ? (
            (() => {
              const info = classifyError(templates.error);
              return (
                <div
                  className={`rounded-lg border p-3 text-xs ${
                    info.kind === 'auth'
                      ? 'bg-yellow-50 border-yellow-200 text-yellow-900'
                      : 'bg-red-50 border-red-200 text-red-900'
                  }`}
                >
                  <p className="font-semibold">{info.title}</p>
                  <p>{info.detail}</p>
                </div>
              );
            })()
          ) : null}

          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
                <tr>
                  <th className="p-3 text-left">Key</th>
                  <th className="p-3 text-left">Channel</th>
                  <th className="p-3 text-left">Module</th>
                  <th className="p-3 text-left">Description</th>
                </tr>
              </thead>
              <tbody>
                {(templates.data?.items ?? []).length === 0 && !templates.isPending ? (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-muted text-xs">
                      No templates found.
                    </td>
                  </tr>
                ) : (
                  (templates.data?.items ?? []).map((t) => (
                    <tr key={t.id} className="border-t border-border hover:bg-gray-50">
                      <td className="p-3 font-mono text-xs">{t.key}</td>
                      <td className="p-3 text-xs">{t.channel}</td>
                      <td className="p-3 text-xs">{t.module}</td>
                      <td className="p-3 text-xs text-muted">{t.description ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
